/**
 * Normalize Google Workspace activity event types to Mycelium backbone names.
 *
 * Activity logs use dot notation (gmail.message_received); connectors expect
 * snake_case Mycelium types (gmail_message_received).
 *
 * @module handlers/integration-event-map
 */

export interface IntegrationPublishTarget {
  domain: string;
  type: string;
}

const DOT_TO_SNAKE: Record<string, IntegrationPublishTarget> = {
  'gmail.message_received': { domain: 'integration', type: 'gmail_message_received' },
  'gmail.message_sent': { domain: 'integration', type: 'gmail_message_sent' },
  'gmail.message_replied': { domain: 'integration', type: 'gmail_message_received' },
  'gmail.message_forwarded': { domain: 'integration', type: 'gmail_message_received' },
  'calendar.event_created': { domain: 'calendar', type: 'event_created' },
  'calendar.event_updated': { domain: 'calendar', type: 'event_updated' },
  'calendar.event_deleted': { domain: 'calendar', type: 'event_deleted' },
  'gtasks.task_created': { domain: 'task', type: 'task_created' },
  'gtasks.task_completed': { domain: 'task', type: 'task_completed' },
  'gtasks.task_updated': { domain: 'task', type: 'task_updated' },
};

/**
 * Map an integration activity event_type to Mycelium domain + type.
 * Falls back to integration domain with snake_case type when unmapped.
 */
export function mapActivityEventToMycelium(
  activityDomain: string,
  eventType: string,
): IntegrationPublishTarget {
  const direct = DOT_TO_SNAKE[eventType];
  if (direct) return direct;

  const snake = eventType.includes('.')
    ? eventType.replace(/\./g, '_')
    : `${activityDomain}_${eventType}`;

  if (activityDomain === 'calendar') {
    return { domain: 'calendar', type: snake.replace(/^calendar_/, '') };
  }
  if (activityDomain === 'gtasks') {
    return { domain: 'task', type: snake.replace(/^gtasks_/, '') };
  }

  return { domain: 'integration', type: snake };
}
